import { useState } from 'react';
import { Layout, Menu, Avatar, Dropdown, Modal, Form, Input, message } from 'antd';
import type { MenuProps } from 'antd';
import {
  DashboardOutlined,
  UserOutlined,
  TeamOutlined,
  SafetyOutlined,
  FileTextOutlined,
  BankOutlined,
  SolutionOutlined,
  PictureOutlined,
  LogoutOutlined,
  SettingOutlined,
  FileSearchOutlined,
  MessageOutlined,
  BarChartOutlined,
  UsergroupAddOutlined,
  InfoCircleOutlined,
  ClockCircleOutlined,
  TrophyOutlined,
  PhoneOutlined,
  ApartmentOutlined,
  FundViewOutlined,
} from '@ant-design/icons';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '@/shared/stores/auth.store';
import { ROUTES } from '@/shared/constants/routes';
import styles from './AdminLayout.module.scss';

const { Header, Sider, Content, Footer } = Layout;

export const AdminLayout = () => {
  const [collapsed, setCollapsed] = useState(false);
  const [profileModalOpen, setProfileModalOpen] = useState(false);
  const [profileForm] = Form.useForm();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, clearAuth } = useAuthStore();

  const handleLogout = () => {
    clearAuth();
    navigate(ROUTES.LOGIN);
  };

  const handleShowProfile = () => {
    profileForm.setFieldsValue({
      realName: user?.realName || '',
      username: user?.username || '',
      roleName: user?.role?.displayName || user?.role?.name || '',
      status: user?.status === 'active' ? '正常' : '禁用',
    });
    setProfileModalOpen(true);
  };

  const handleProfileSave = async () => {
    try {
      await profileForm.validateFields();
      // 这里可以调用API更新用户信息
      message.success('个人信息已更新');
      setProfileModalOpen(false);
    } catch (error) {
      console.error('Form validation failed:', error);
    }
  };

  const handleUserMenuClick: MenuProps['onClick'] = ({ key }) => {
    switch (key) {
      case 'profile':
        handleShowProfile();
        break;
      case 'logout':
        handleLogout();
        break;
    }
  };

  const userMenuItems: MenuProps['items'] = [
    {
      key: 'profile',
      icon: <UserOutlined />,
      label: '个人信息',
    },
    {
      type: 'divider',
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: '退出登录',
    },
  ];

  const menuItems: MenuProps['items'] = [
    {
      key: ROUTES.DASHBOARD,
      icon: <DashboardOutlined />,
      label: '仪表盘',
      onClick: () => navigate(ROUTES.DASHBOARD),
    },
    {
      key: ROUTES.DATA_ANALYTICS,
      icon: <FundViewOutlined />,
      label: '数据分析',
      onClick: () => navigate(ROUTES.DATA_ANALYTICS),
    },
    {
      key: 'content',
      icon: <FileTextOutlined />,
      label: '内容管理',
      children: [
        {
          key: ROUTES.BANNERS,
          icon: <PictureOutlined />,
          label: 'Banner管理',
          onClick: () => navigate(ROUTES.BANNERS),
        },
        {
          key: ROUTES.NEWS,
          icon: <FileTextOutlined />,
          label: '新闻管理',
          onClick: () => navigate(ROUTES.NEWS),
        },
        {
          key: ROUTES.PARTNERS,
          icon: <UsergroupAddOutlined />,
          label: '合作伙伴',
          onClick: () => navigate(ROUTES.PARTNERS),
        },
        {
          key: ROUTES.FEEDBACK,
          icon: <MessageOutlined />,
          label: '反馈管理',
          onClick: () => navigate(ROUTES.FEEDBACK),
        },
        {
          key: '/admin/team-gallery',
          icon: <TeamOutlined />,
          label: '团队风采',
          onClick: () => navigate('/admin/team-gallery'),
        },
      ],
    },
    {
      key: 'recruitment',
      icon: <SolutionOutlined />,
      label: '人才招聘',
      children: [
        {
          key: '/admin/recruitment/social',
          icon: <TeamOutlined />,
          label: '社招管理',
          onClick: () => navigate('/admin/recruitment/social'),
        },
        {
          key: '/admin/recruitment/campus',
          icon: <UserOutlined />,
          label: '校招管理',
          onClick: () => navigate('/admin/recruitment/campus'),
        },
        {
          key: '/admin/recruitment/cities',
          icon: <BankOutlined />,
          label: '城市管理',
          onClick: () => navigate('/admin/recruitment/cities'),
        },
        {
          key: '/admin/recruitment/job-types',
          icon: <SolutionOutlined />,
          label: '职位类型',
          onClick: () => navigate('/admin/recruitment/job-types'),
        },
      ],
    },
    {
      key: 'investor',
      icon: <BankOutlined />,
      label: '投资者关系',
      children: [
        {
          key: ROUTES.FINANCIAL_REPORTS,
          icon: <FileTextOutlined />,
          label: '财报管理',
          onClick: () => navigate(ROUTES.FINANCIAL_REPORTS),
        },
        {
          key: ROUTES.GOVERNANCE,
          icon: <SafetyOutlined />,
          label: '治理文件管理',
          onClick: () => navigate(ROUTES.GOVERNANCE),
        },
        {
          key: '/admin/reporting-channel',
          icon: <PhoneOutlined />,
          label: '举报渠道配置',
          onClick: () => navigate('/admin/reporting-channel'),
        },
        {
          key: '/admin/announcements',
          icon: <FileTextOutlined />,
          label: '公告及通函',
          onClick: () => navigate('/admin/announcements'),
        },
      ],
    },
    {
      key: 'about',
      icon: <InfoCircleOutlined />,
      label: '关于创美',
      children: [
        {
          key: '/admin/content/home-about',
          icon: <InfoCircleOutlined />,
          label: '首页关于创美',
          onClick: () => navigate('/admin/content/home-about'),
        },
        {
          key: '/admin/content/about-company-intro',
          icon: <InfoCircleOutlined />,
          label: '关于页关于创美',
          onClick: () => navigate('/admin/content/about-company-intro'),
        },
        {
          key: '/admin/advantages',
          icon: <TrophyOutlined />,
          label: '核心优势管理',
          onClick: () => navigate('/admin/advantages'),
        },
        {
          key: ROUTES.MILESTONES,
          icon: <ClockCircleOutlined />,
          label: '发展历程管理',
          onClick: () => navigate(ROUTES.MILESTONES),
        },
        {
          key: ROUTES.HONORS,
          icon: <TrophyOutlined />,
          label: '荣誉资质管理',
          onClick: () => navigate(ROUTES.HONORS),
        },
        {
          key: ROUTES.ORG_STRUCTURE,
          icon: <ApartmentOutlined />,
          label: '组织架构管理',
          onClick: () => navigate(ROUTES.ORG_STRUCTURE),
        },
        {
          key: '/admin/companies',
          icon: <BankOutlined />,
          label: '公司管理',
          onClick: () => navigate('/admin/companies'),
        },
      ],
    },
    {
      key: 'system',
      icon: <SettingOutlined />,
      label: '系统管理',
      children: [
        {
          key: ROUTES.USERS,
          icon: <UserOutlined />,
          label: '用户管理',
          onClick: () => navigate(ROUTES.USERS),
        },
        {
          key: ROUTES.ROLES,
          icon: <TeamOutlined />,
          label: '角色管理',
          onClick: () => navigate(ROUTES.ROLES),
        },
        {
          key: ROUTES.PERMISSIONS,
          icon: <SafetyOutlined />,
          label: '权限管理',
          onClick: () => navigate(ROUTES.PERMISSIONS),
        },
        {
          key: ROUTES.AUDIT_LOGS,
          icon: <FileSearchOutlined />,
          label: '操作日志',
          onClick: () => navigate(ROUTES.AUDIT_LOGS),
        },
        {
          key: ROUTES.ANALYTICS,
          icon: <BarChartOutlined />,
          label: '数据分析',
          onClick: () => navigate(ROUTES.ANALYTICS),
        },
      ],
    },
  ];

  const getSelectedKeys = () => {
    const path = location.pathname;
    if (path === '/admin' || path === '/admin/') return [ROUTES.DASHBOARD];
    return [path];
  };

  const getOpenKeys = () => {
    const path = location.pathname;
    if (path.startsWith('/admin/users') || path.startsWith('/admin/roles') || 
        path.startsWith('/admin/permissions') || path.startsWith('/admin/audit-logs') ||
        (path.startsWith('/admin/analytics') && !path.startsWith('/admin/data-analytics'))) {
      return ['system'];
    }
    if (path.startsWith('/admin/banners') || path.startsWith('/admin/news') || 
        path.startsWith('/admin/partners') || path.startsWith('/admin/feedback') ||
        path.startsWith('/admin/team-gallery')) {
      return ['content'];
    }
    if (path.startsWith('/admin/jobs') || path.startsWith('/admin/recruitment')) {
      return ['recruitment'];
    }
    if (path.startsWith('/admin/financial-reports') || path.startsWith('/admin/governance') || path.startsWith('/admin/reporting-channel') || path.startsWith('/admin/announcements')) {
      return ['investor'];
    }
    if (path.startsWith('/admin/milestones') || path.startsWith('/admin/honors') ||
        path.startsWith('/admin/content/home-about') || path.startsWith('/admin/content/about-company-intro') || path.startsWith('/admin/advantages') ||
        path.startsWith('/admin/org-structure')) {
      return ['about'];
    }
    return [];
  };

  return (
    <Layout className={styles.adminLayout}>
      <Sider
        collapsible
        collapsed={collapsed}
        onCollapse={setCollapsed}
        width={240}
        style={{
          overflow: 'auto',
          height: '100vh',
          position: 'fixed',
          left: 0,
          top: 0,
          bottom: 0,
        }}
      >
        <div className={styles.logo}>
          {!collapsed ? '创美药业管理系统' : '创美'}
        </div>
        <Menu
          theme="dark"
          mode="inline"
          selectedKeys={getSelectedKeys()}
          defaultOpenKeys={getOpenKeys()}
          items={menuItems}
        />
      </Sider>
      <Layout style={{ marginLeft: collapsed ? 80 : 240, transition: 'margin-left 0.2s' }}>
        <Header className={styles.header}>
          <div className={styles.headerTitle}>创美药业管理系统 V2</div>
          <Dropdown menu={{ items: userMenuItems, onClick: handleUserMenuClick }} placement="bottomRight">
            <div className={styles.userInfo}>
              <Avatar icon={<UserOutlined />} src={user?.avatar} />
              <span className={styles.userName}>{user?.realName || user?.username || '管理员'}</span>
            </div>
          </Dropdown>
        </Header>
        <Content className={styles.content}>
          <Outlet />
        </Content>
        <Footer className={styles.footer}>
          <div className={styles.footerContent}>
            <a 
              href="https://beian.mps.gov.cn/#/query/webSearch" 
              target="_blank" 
              rel="noopener noreferrer"
              className={styles.policeLink}
            >
              粤公网安备 44060402002640号
            </a>
          </div>
        </Footer>
      </Layout>

      {/* 个人信息弹窗 */}
      <Modal
        title="个人信息"
        open={profileModalOpen}
        onOk={handleProfileSave}
        onCancel={() => setProfileModalOpen(false)}
        okText="保存"
        cancelText="取消"
        width={500}
      >
        <Form form={profileForm} layout="vertical">
          <Form.Item name="realName" label="姓名">
            <Input placeholder="请输入姓名" />
          </Form.Item>
          <Form.Item name="username" label="用户名">
            <Input disabled />
          </Form.Item>
          <Form.Item name="roleName" label="角色">
            <Input disabled />
          </Form.Item>
          <Form.Item name="status" label="状态">
            <Input disabled />
          </Form.Item>
        </Form>
      </Modal>
    </Layout>
  );
};
